import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { config } from '../constants/config'

// export const fetchArticles = createAsyncThunk(
//   'articles/fetchArticles',
//   async (props) => {
//     let response = await axios.get(config.GET_API_ARTICLES, {
//       params: {
//         uuid: props.uuid,
//         reqCategories: props.reqCategories,
//       }
//     });
//     return response.data;
//   }
// );
export const fetchArticles = createAsyncThunk('articles/fetchArticles', async (props) => {
  const response = await axios.get(config.GET_API_ARTICLES, { params: props });
  return response.data;
})

export const fetchArticle = createAsyncThunk(
  'articles/fetchArticle',
  async ({ uuid }) => {
    let query = `${config.GET_API_ARTICLE}`
    const response = await axios.get(query);
    return response.data;
  }
);

export const fetchArticleContent = createAsyncThunk(
  'articles/fetchArticleContent',
  async ({ articleUuid, contentUrl }, { getState, dispatch }) => {
    const response = await fetch(contentUrl);
    const content = await response.text();

    return { articleUuid, content };
  }
);


export const deleteArticle = createAsyncThunk(
  'articles/deleteArticle',
  async ({ uuid }) => {
    /**
     * ⛔ THE TOKEN IS NEW, AND IT IS WHY THIS WORKED BEFORE. The delete route carried no guard, so
     * a bare axios call with no credentials succeeded — from anywhere, for anyone. The route is now
     * behind adminAuth, which this has to satisfy the same way `addArticle` below always did.
     */
    const token = localStorage.getItem('authToken');
    const response = await axios.delete(`${config.DELETE_API_ARTICLE}/${uuid}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  }
);

export const addArticle = createAsyncThunk(
  'articles/addArticle',
  async ({ uuid, name, enabled, author, categories, excerpt, heroImageUrl, thumbnailImageUrl, contentUrl, content, emailSubtype, emailSubject }) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      const instance = axios.create({
        baseURL: config.BASE_URL,  // specify your base URL
        headers: { 'Authorization': `Bearer ${token}` }
      });

      try {
        /**
         * ⓘ emailSubtype / emailSubject are undefined for every ordinary article, and the server
         * coerces them to explicit nulls — an ABSENT Neo4j parameter is not null, it refuses the
         * statement outright, which is how the first recipient registration 500ed over $phone.
         */
        const response = await instance.post(config.ADD_API_ARTICLE, {
          uuid, name, enabled, author, categories, excerpt, heroImageUrl, thumbnailImageUrl,
          contentUrl, content, emailSubtype, emailSubject,
        });
        return response.data;
      } catch (error) {
        /**
         * ⛔ SWALLOWING THIS RESOLVED THE THUNK WITH undefined, and the fulfilled reducer then
         * assigned undefined over state.articles — so the next render of ArticleEditor read
         * `state.core.articles.articles` off undefined and took the whole page down through the
         * router's ErrorBoundary. A failed save became a crash one tick later, which is why the
         * 500 and the TypeError arrived together and the TypeError got the blame.
         */
        console.error('Error saving article:', error.response && error.response.data);
        throw error;
      }

    }
    else {
      console.error('Error saving article: Invalid user token');
      throw new Error('Invalid user token');

    }

  }
);

// Async thunk action to refresh S3 credentials
// (refreshS3Credentials removed 2026-09-08 — the endpoint it called signed a legacy S3 POST
//  policy with the raw AWS secret key, which production does not have and cannot have with an
//  instance role. Its only consumer posted the result to a placeholder URL.)

// const selectArticlesState = state => state.articles;

// export const selectAllArticles = createSelector(
//   selectArticlesState,
//   (articleState) => {
//     if (articleState.fetchArticlestatus === "succeeded" ||
//       articleState.addArticlestatus === "succeeded") {
//       try {
//         return articleState.articles;
//       } catch (e) {
//         console.error('No articles', e.message);
//         return [];
//       }
//     }
//     else {
//       // console.error('Current article.fetchArticlestatus', articleState.fetchArticlestatus);
//       return [];
//     }
//   }
// );

/**
 * ⭐ THE LIST SHAPE IS { articles: [...] }, and only a payload carrying that may replace it.
 *
 * ⓘ addArticle and deleteArticle resolve with the SERVER's reply — { articleItem } for a save —
 * which is not a list at all. Assigning it blindly left state.articles.articles undefined and every
 * consumer of the list reading a property off undefined. The components refetch after a write, so
 * keeping the previous list until that arrives is both safe and what they already expect.
 */
const keepListShape = (state, payload) => {
  if (payload && Array.isArray(payload.articles)) state.articles = payload;
};
const articlesSlice = createSlice({
  name: 'articles',
  initialState: {
    /**
     * ⛔ `{ articles: [] }`, NOT `[]`. All 30 consumers read `state.core.articles.articles`, and
     * none treats this as an array — so an initial `[]` made that path `undefined` until the first
     * successful fetch. Every reader then had to guess: ArticleEditor read `.articles` off it and
     * crashed, fetchArticleContent called `.find` on it and crashed, the sidebar rendered a
     * heading over nothing.
     *
     * ⚠️ The window is small and real: a failed or slow get-articles, or any dispatch that lands
     * before it. The shape should not depend on a request having succeeded.
     */
    articles: { articles: [] },
    sharedUrl: null,
    status: 'idle',
    addArticleStatus: 'idle',
    deleteArticleStatus: 'idle',
    fetchArticleStatus: 'idle',
    fetchArticlesStatus: 'idle',
    activeArticle: null,
    error: null,
    s3Policy: '',
    s3Signature: '',
  },
  reducers: {
    shareSignedUrl: (state, action) => {
      state.sharedUrl = action.payload;
    },
    setActiveArticle: (state, action) => {
      // console.log('setActiveArticle', action)
      state.activeArticle = action.payload;;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchArticles.pending, (state) => {
        state.fetchArticlesStatus = 'loading';
      })
      .addCase(fetchArticles.fulfilled, (state, action) => {
        state.fetchArticlesStatus = 'succeeded';
        keepListShape(state, action.payload);
      })
      .addCase(fetchArticles.rejected, (state, action) => {
        state.fetchArticlesStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchArticle.pending, (state) => {
        state.fetchArticleStatus = 'loading';
      })
      .addCase(fetchArticle.fulfilled, (state, action) => {
        state.fetchArticleStatus = 'succeeded';
        keepListShape(state, action.payload);
      })
      .addCase(fetchArticle.rejected, (state, action) => {
        state.fetchArticleStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(addArticle.pending, (state) => {
        state.addArticleStatus = 'loading';
      })
      .addCase(addArticle.fulfilled, (state, action) => {
        state.addArticleStatus = 'succeeded';
        keepListShape(state, action.payload);
      })
      .addCase(addArticle.rejected, (state, action) => {
        state.addArticleStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteArticle.pending, (state) => {
        state.deleteArticleStatus = 'deleting';
      })
      .addCase(deleteArticle.fulfilled, (state, action) => {
        state.deleteArticleStatus = 'succeeded';
        keepListShape(state, action.payload);
      })
      .addCase(deleteArticle.rejected, (state, action) => {
        state.deleteArticleStatus = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchArticleContent.fulfilled, (state, action) => {
        const { articleUuid, content } = action.payload;
        // ⓘ Belt as well as braces: the body can arrive before, or without, the list it belongs to.
        const list = state.articles && state.articles.articles;
        if (!Array.isArray(list)) return;
        const article = list.find((a) => a.uuid === articleUuid);
        if (article) {
          article.content = content;
        }
      })
      ;
  },
});

export const { shareSignedUrl } = articlesSlice.actions
export default articlesSlice.reducer;

export const selectShareSignedUrl = (state) => state.user.articles && state.articles.shareSignedUrl;
// export const selectAllArticles = (state) => state.articles && state.articles.articles;
export const selectArticlesStatus = (state) => state.articles.status;
export const selectArticleContent = (state, articleUuid) => {
  let article = null;
  if (state.core.articles && state.core.articles.articles && articleUuid) {
    article = state.core.articles.articles.find(article => article.uuid === articleUuid);
  }
  else {
    // console.log('not found selectArticleContent', articleUuid)
  }
  return article ? article.content : null;
};
export const selectActiveArticle = (state) => {
  if (state.articles) return state.articles.activeArticle;
  return null
}
export const { setActiveArticle } = articlesSlice.actions;
export const getArticleByUuid = (state, articleUuid) => {
  let article = null;
  if (state.articles && state.articles.articles && articleUuid) {
    article = state.articles.articles.find(article => article.uuid === articleUuid);
  }
  else {
    // console.log('not found selectArticleContent', articleUuid)
  }
  return article ? article : null;
}
